import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Sparkles, Wrench, User, LogOut, LogIn } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDevMode } from "@/hooks/useDevMode";
import { useAuth } from "@/hooks/useAuth";
import ThemeToggle from "@/components/theme/ThemeToggle";

const links = [
  { to: "/subjects", label: "Materii" },
  { to: "/materiale", label: "Bibliotecă" },
  { to: "/news", label: "Noutăți", showBadge: true },
  { to: "/pricing", label: "Beta gratuită" },
  { to: "/misiune", label: "Despre & Misiune" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [hasNewNews, setHasNewNews] = useState(false);
  const { isDev } = useDevMode();
  const navigate = useNavigate();

  useEffect(() => {
    let scrollRaf = 0;
    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0;
        setScrolled(window.scrollY > 8);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // Check for unread news
    supabase
      .from("news_posts")
      .select("created_at")
      .eq("published", true)
      .order("created_at", { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (data && data[0]) {
          const lastSeen = localStorage.getItem("news_last_seen");
          if (!lastSeen || new Date(data[0].created_at) > new Date(lastSeen)) {
            setHasNewNews(true);
          }
        }
      });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="sticky top-0 z-50 w-full">
      {/* Announcement marquee */}
      <div className="overflow-hidden border-b border-ink/10 bg-ink py-2 text-ink-foreground">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0 items-center gap-10 px-5 text-xs font-mono uppercase tracking-widest">
              <span className="flex items-center gap-2"><Sparkles className="h-3 w-3 text-accent" /> Beta deschisă — gratis pentru toți</span>
              <span aria-hidden>✦</span>
              <span>Limba română — disponibilă acum</span>
              <span aria-hidden>✦</span>
              <span>Făcut în România · 2026</span>
              <span aria-hidden>✦</span>
              <span>Materii noi în curând</span>
              <span aria-hidden>✦</span>
            </div>
          ))}
        </div>
      </div>

      <header
        className={`w-full transition-smooth ${
          scrolled
            ? "border-b border-border/60 bg-background/80 backdrop-blur-xl"
            : "border-b border-transparent bg-background"
        }`}
      >
        <div className="container flex h-16 items-center justify-between">
          <Link to="/" className="group flex items-center gap-2.5">
            <span className="relative grid h-9 w-9 place-items-center rounded-2xl bg-ink text-ink-foreground shadow-card transition-spring group-hover:rotate-12">
              <span className="font-display text-lg font-bold leading-none">se</span>
              <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-accent" />
            </span>
            <span className="font-display text-lg font-semibold tracking-tight">Self&nbsp;Educate</span>
          </Link>

          <nav className="hidden items-center gap-0.5 md:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `relative rounded-full px-4 py-2 text-sm font-medium transition-smooth hover:text-foreground ${
                    isActive ? "text-foreground" : "text-muted-foreground"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {l.showBadge && hasNewNews && (
                      <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                      </span>
                    )}
                    {isActive && (
                      <span className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-accent" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <ThemeToggle />
            {isDev && (
              <Button asChild variant="outline" size="sm" className="border-ink/30">
                <Link to="/dev"><Wrench className="h-3 w-3" /> Dev</Link>
              </Button>
            )}
            {user ? (
              <>
                <Button asChild variant="ghost" size="sm" className="gap-1.5">
                  <Link to="/account/billing">Facturare</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-1.5 border-ink/30">
                  <Link to="/dashboard"><User className="h-4 w-4" /> Contul meu</Link>
                </Button>
                <Button onClick={handleSignOut} variant="ghost" size="sm" aria-label="Ieșire">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="outline" size="sm" className="gap-1.5 border-ink/30">
                  <Link to="/auth"><LogIn className="h-4 w-4" /> Intră în cont</Link>
                </Button>
                <Button asChild variant="warm" size="sm"><Link to="/pricing">Începe gratis</Link></Button>
              </>
            )}
          </div>

          <div className="flex items-center gap-1 md:hidden">
            <ThemeToggle />
            <button onClick={() => setOpen(!open)} aria-label="Meniu">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t border-border bg-background md:hidden">
            <nav className="container flex flex-col gap-1 py-4">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-muted">
                  {l.label}
                </Link>
              ))}
              <div className="mt-2 flex gap-2">
                {user ? (
                  <>
                    <Button asChild variant="outline" className="flex-1" onClick={() => setOpen(false)}>
                      <Link to="/dashboard"><User className="h-4 w-4" /> Contul meu</Link>
                    </Button>
                    <Button asChild variant="ghost" className="flex-1" onClick={() => setOpen(false)}>
                      <Link to="/account/billing">Facturare</Link>
                    </Button>
                    <Button onClick={() => { setOpen(false); handleSignOut(); }} variant="ghost" size="icon" aria-label="Ieșire">
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild variant="outline" className="flex-1" onClick={() => setOpen(false)}>
                      <Link to="/auth"><LogIn className="h-4 w-4" /> Intră</Link>
                    </Button>
                    <Button asChild variant="warm" className="flex-1" onClick={() => setOpen(false)}>
                      <Link to="/pricing">Începe</Link>
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>
    </div>
  );
};

export default Navbar;
